import { API_ROUTES } from '@shared/api-utils';
import type { PromoType } from '../types/types';

type PromotionUrls = {
  claim?: string;
  list: string;
};

type PromotionConfig = {
  claimRoute?: string;
  claimIdParam?: string;
  listRoute: string;
  statusMapping?: Record<string, string>;
  supportsClaim: boolean;
};

type PromoTypeWithoutOffers = Exclude<PromoType, 'offers'>;

let PROMOTION_CONFIG_MAP: Record<PromoTypeWithoutOffers, PromotionConfig> | null = null;

const getPromotionConfigMap = (): Record<PromoTypeWithoutOffers, PromotionConfig> => {
  if (!PROMOTION_CONFIG_MAP) {
    PROMOTION_CONFIG_MAP = {
      'free-spins': {
        claimRoute: API_ROUTES.PROMOTIONS.FREE_SPINS_CLAIM,
        claimIdParam: ':freeSpinsId',
        listRoute: API_ROUTES.PROMOTIONS.FREE_SPINS,
        supportsClaim: true,
      },
      bonuses: {
        claimRoute: API_ROUTES.PROMOTIONS.BONUS_FUNDS_CLAIM,
        claimIdParam: ':bonusFundId',
        listRoute: API_ROUTES.PROMOTIONS.BONUS_FUNDS,
        supportsClaim: true,
      },
    };
  }
  return PROMOTION_CONFIG_MAP;
};

/**
 * Gets promotion URLs based on type and parameters.
 *
 * @param promoType - The type of promotion ('free-spins' | 'bonuses' | 'offers')
 * @param params - Parameters for URL generation
 * @returns Object with claim and list URLs
 */
function getPromotionUrls(
  promoType: PromoType,
  params: {
    itemId?: string;
    status?: 'available' | 'previous' | 'active' | 'non-active';
    limit?: string;
    offset?: string;
    filteredVertical?: string[];
  } = {}
): PromotionUrls {
  const { itemId, status = 'active', limit = '5', offset = '0', filteredVertical = [] } = params;

  // 'offers' type doesn't have a config in the map, return empty URLs
  if (promoType === 'offers') {
    return {
      claim: undefined,
      list: '',
    };
  }

  const config = getPromotionConfigMap()[promoType as Exclude<PromoType, 'offers'>];
  if (!config) {
    return {
      claim: undefined,
      list: '',
    };
  }

  const claimUrl = config.supportsClaim && itemId && config.claimRoute && config.claimIdParam ? config.claimRoute.replace(config.claimIdParam, itemId) : '';
  const mappedStatus = config.statusMapping?.[status] || status;

  let listUrl = config.listRoute.replace(':status', mappedStatus);
  // bonus endpoint just allow one vertical at a time
  listUrl = listUrl.replace(':vertical', (filteredVertical[0] as string) || '');
  listUrl = listUrl.replace(':offset', offset || '');
  listUrl = listUrl.replace(':limit', limit || '');

  return {
    claim: claimUrl,
    list: listUrl,
  };
}

/**
 * Gets the active list URL used for cache invalidation after mutations.
 * This ensures the invalidation matches the exact query used in components.
 *
 * @param promoType - The type of promotion
 * @returns The URL used for the active list query
 */
function getActiveUrlForInvalidation(promoType: PromoType): string {
  switch (promoType) {
    case 'free-spins':
      return getPromotionUrls('free-spins', { status: 'active', offset: '0', limit: '10' }).list;
    case 'bonuses':
      return getPromotionUrls('bonuses', { status: 'active', offset: '0', limit: '10' }).list;
    default:
      return '';
  }
}

/**
 * Checks if a promotion type supports claim functionality.
 *
 * @param promoType - The type of promotion
 * @returns True if the promotion type supports claiming
 */
function supportsClaimAction(promoType: PromoType): boolean {
  const config = getPromotionConfigMap()[promoType as Exclude<PromoType, 'offers'>];
  return config?.supportsClaim || false;
}

export { getActiveUrlForInvalidation, getPromotionUrls, supportsClaimAction };
export type { PromotionUrls };
